import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Backdrop, Box, ButtonBase, CircularProgress, Menu, MenuItem, styled } from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { selectUserOrgId, selectUserRole } from '../../auth/redux/slice';
import { ROUTES, USER_ROLES } from '../../shared/constants';
import { loadSwitcherOrganizations, selectSwitcherOrganizations, selectSwitcherIsBusy, selectSwitcherLastSwitchedOrg, switchOrg } from '../redux/slice';
import { OrgSwitchedDialog } from './OrgSwitchedDialog';
import { HEADER_ACCENT } from '../../shared/components/Header.styled';

// Same pill/border language as Header.styled's UserButton, so this reads as part of the same
// family of header controls rather than a generic MUI dropdown dropped in next to them. Aug 2026 -
// pulls the shared HEADER_ACCENT constant from Header.styled.tsx instead of its own hand-rolled
// hex, so this and every header control stay in sync if the accent ever changes again.
// alignSelf: 'center' is needed now that Header's own alignItems is 'stretch' (so the active nav
// tab's underline can sit flush with the header's bottom edge) - without it this pill would
// stretch to the full header height instead of staying its own compact size.
const SwitcherButton = styled(ButtonBase)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    gap: '8px',
    padding: '7px 12px',
    borderRadius: '999px',
    border: `1px solid ${HEADER_ACCENT}`,
    backgroundColor: 'transparent',
    color: HEADER_ACCENT,
    fontWeight: 600,
    fontSize: '13px',
    fontFamily: '"Inter", sans-serif',
    marginRight: theme.spacing(1.5),
    maxWidth: '220px',
    flexShrink: 0,
    transition: 'background-color 0.15s ease',
    '&:hover': { backgroundColor: 'rgba(8,145,178,0.08)' },
    '&.Mui-disabled': { opacity: 0.6 },
    [theme.breakpoints.down('md')]: { display: 'none' },
}));

const SwitcherLabel = styled('span')({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

// Org Switcher feature - lets a User Group Manager pick which org (among the orgs sharing their
// UserGroupId) they're currently acting as. Only rendered for that role, and only once we know
// there's actually more than one org to switch between - a switcher with a single, forced option
// is just clutter. Selecting a different org reissues a token scoped to it (see orgSwitcher/sagas),
// so every other org-scoped part of the app picks up the change automatically.
export const OrgSwitcher: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userRole = useSelector(selectUserRole);
    const currentOrgId = useSelector(selectUserOrgId);
    const organizations = useSelector(selectSwitcherOrganizations);
    const isBusy = useSelector(selectSwitcherIsBusy);
    const lastSwitchedOrg = useSelector(selectSwitcherLastSwitchedOrg);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const isEligible = userRole === USER_ROLES.UserGroupManager;

    useEffect(() => {
        if (isEligible)
            dispatch(loadSwitcherOrganizations());
    }, [dispatch, isEligible]);

    // The switcher is reachable from every page, and everything on the current page was scoped
    // to the org just left behind - rather than leave the user looking at (or worse, acting on)
    // now-stale data for a page that may not even apply to the new org, land them somewhere
    // that's always valid and refetches fresh: home.
    useEffect(() => {
        if (lastSwitchedOrg)
            navigate(ROUTES.Dashboard);
    }, [lastSwitchedOrg, navigate]);

    if (!isEligible || organizations.length < 2)
        return null;

    const currentOrg = organizations.find(o => o.orgId?.toString() === currentOrgId);

    const handleSelect = (orgId?: number) => {
        setMenuAnchor(null);
        if (orgId && orgId.toString() !== currentOrgId)
            dispatch(switchOrg(orgId));
    };

    return (
        <>
            <SwitcherButton
                onClick={(e) => setMenuAnchor(e.currentTarget as HTMLElement)}
                disabled={isBusy}
                aria-label="Switch organization"
            >
                <ApartmentOutlinedIcon sx={{ fontSize: '16px', flexShrink: 0 }} />
                <SwitcherLabel>{currentOrg?.name ?? 'Select organization'}</SwitcherLabel>
                <KeyboardArrowDownIcon sx={{ fontSize: '16px', flexShrink: 0 }} />
            </SwitcherButton>

            {/* Same Menu/MenuItem treatment as the user profile dropdown in Header.tsx, so the two
                header-level menus read as one consistent design rather than two different pickers. */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        mt: '8px', minWidth: 260, maxWidth: 340, maxHeight: '70vh',
                        borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                    },
                }}
            >
                {organizations.map(org => {
                    const selected = org.orgId?.toString() === currentOrgId;
                    return (
                        <MenuItem
                            key={org.orgId}
                            selected={selected}
                            onClick={() => handleSelect(org.orgId)}
                            sx={{
                                px: '16px', py: '10px', gap: '12px', fontSize: '13px',
                                fontWeight: selected ? 700 : 500,
                                color: selected ? HEADER_ACCENT : '#1A2332',
                                fontFamily: '"Inter", sans-serif',
                                justifyContent: 'space-between',
                                '&:hover': { backgroundColor: '#F0FAFB' },
                                '&.Mui-selected': { backgroundColor: 'rgba(8,145,178,0.08)' },
                                '&.Mui-selected:hover': { backgroundColor: 'rgba(8,145,178,0.12)' },
                            }}
                        >
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {org.name}
                            </Box>
                            {selected && <CheckIcon sx={{ fontSize: '16px', flexShrink: 0 }} />}
                        </MenuItem>
                    );
                })}
            </Menu>

            {/* Switching orgs changes context for the whole app (nav, dashboard stats, Pre-Buy
                gating, etc.) - block interaction until the new token is in and everything that reads
                off it has a chance to refetch, rather than letting the user click into a page mid-switch
                and see a confusing mix of old/new org data. */}
            <Backdrop
                open={isBusy}
                transitionDuration={{ enter: 200, exit: 300 }}
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 10,
                    backgroundColor: 'rgba(13,27,42,0.55)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                <CircularProgress sx={{ color: '#5FD4EA' }} />
                <Box sx={{ fontSize: '14px', fontWeight: 600, fontFamily: '"Inter", sans-serif' }}>
                    Switching organization&hellip;
                </Box>
            </Backdrop>

            <OrgSwitchedDialog org={lastSwitchedOrg} />
        </>
    );
};
